import { Link } from "react-router-dom";
import { useEffect } from "react";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://allchinabuy.team";

const articleStructuredData = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "The Complete AllChinaBuy Spreadsheet Guide: How to Source Products from China Like a Pro in 2026",
  "description": "A comprehensive walkthrough of the AllChinaBuy Spreadsheet system that helps dropshippers, resellers, and e-commerce entrepreneurs find verified China suppliers, compare buying agents, and make data-driven purchasing decisions.",
  "url": `${siteUrl}/allchinabuy-spreadsheet-guide`,
  "datePublished": "2026-06-20",
  "dateModified": "2026-06-25",
  "author": {
    "@type": "Organization",
    "name": "AllChinaBuy Spreadsheet",
    "url": siteUrl
  },
  "publisher": {
    "@type": "Organization",
    "name": "AllChinaBuy Spreadsheet",
    "url": siteUrl,
    "logo": {
      "@type": "ImageObject",
      "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg"
    }
  }
};

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Is the AllChinaBuy Spreadsheet free to use?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Access to the spreadsheet data is available through the main AllChinaBuy platform. The spreadsheet serves as your research and comparison tool, while actual orders are processed on the main site where standard platform fees apply."
      }
    },
    {
      "@type": "Question",
      "name": "How often is supplier data updated?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The AllChinaBuy Spreadsheet follows a weekly update schedule. Pricing data, supplier availability, and agent comparison metrics are refreshed every seven days to ensure the information you are working with is current and accurate."
      }
    },
    {
      "@type": "Question",
      "name": "Can I use the AllChinaBuy Spreadsheet for any product type?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The spreadsheet currently covers 14 major product categories spanning fashion, accessories, and lifestyle products. If your target product falls outside these categories, you can still use the agent comparison data to identify reliable buying agents for your specific sourcing needs."
      }
    },
    {
      "@type": "Question",
      "name": "How does the AllChinaBuy Spreadsheet differ from using an agent directly?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Using an agent directly means you are limited to that single agent's pricing, shipping options, and supplier relationships. The spreadsheet approach lets you compare multiple agents simultaneously, so you can choose the best option for each specific order rather than being locked into one provider."
      }
    }
  ]
};

export default function ArticlePage() {
  useEffect(() => {
    const articleScript = document.createElement("script");
    articleScript.type = "application/ld+json";
    articleScript.textContent = JSON.stringify(articleStructuredData);
    document.head.appendChild(articleScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify(faqStructuredData);
    document.head.appendChild(faqScript);

    return () => {
      if (articleScript.parentNode) articleScript.parentNode.removeChild(articleScript);
      if (faqScript.parentNode) faqScript.parentNode.removeChild(faqScript);
    };
  }, []);
  return (
    <div className="min-h-screen bg-background-50">
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-background-50 text-base"></i>
              </div>
              <span className="font-heading font-semibold text-foreground-950 text-base whitespace-nowrap">
                AllChinaBuy Sheet
              </span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
            <span className="text-sm text-foreground-500">AllChinaBuy Spreadsheet Guide</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            The Complete AllChinaBuy Spreadsheet Guide: How to Source Products from China Like a Pro in 2026
          </h1>
          <p className="text-foreground-600 text-base leading-relaxed mb-4">
            A comprehensive walkthrough of the AllChinaBuy Spreadsheet system — the structured sourcing intelligence platform that helps dropshippers, resellers, and e-commerce entrepreneurs find verified China suppliers, compare buying agents, and make data-driven purchasing decisions.
          </p>
          <div className="flex items-center gap-4 text-sm text-foreground-500">
            <span>Updated: June 2026</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>8 min read</span>
          </div>
        </header>

        <div className="relative rounded-2xl overflow-hidden mb-12">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20dual%20monitors%20showing%20spreadsheets%20and%20analytics%2C%20surrounded%20by%20product%20samples%20including%20a%20watch%20sunglasses%20and%20fabric%20swatches%20on%20a%20clean%20white%20desk%2C%20bright%20natural%20light%20from%20floor%20to%20ceiling%20windows%2C%20warm%20walnut%20wood%20accents%2C%20professional%20yet%20creative%20atmosphere%2C%20no%20visible%20screen%20text%2C%20contemporary%20studio%20aesthetic&width=1200&height=600&seq=guide-v2-hero&orientation=landscape"
            alt="AllChinaBuy Spreadsheet sourcing guide"
            title="Complete AllChinaBuy Spreadsheet Sourcing Guide 2026"
            className="w-full h-auto object-cover"
          />
        </div>

        <div className="prose-custom">
          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Why the AllChinaBuy Spreadsheet Changes Everything for China Sourcing
            </h2>
            <p className="text-foreground-700 text-base leading-relaxed mb-4">
              If you have ever tried sourcing products from China through platforms like 1688, Taobao, or various agent sites, you already know the frustration. Information is scattered across dozens of pages, agent fees are hidden behind vague pricing structures, and supplier verification feels like guesswork. The <strong>AllChinaBuy Spreadsheet</strong> was built to solve exactly these problems by centralizing everything into one structured, searchable, and regularly updated database.
            </p>
            <p className="text-foreground-700 text-base leading-relaxed mb-4">
              What makes the <strong>AllChinaBuy Spreadsheet</strong> fundamentally different from traditional agent websites is its organizational philosophy. Instead of forcing you to click through endless product pages or agent profiles, it presents verified supplier data, agent comparison metrics, pricing timelines, and shipping estimates in a clean spreadsheet format that you can filter, sort, and analyze at a glance. For anyone running a dropshipping business, reselling operation, or e-commerce store that relies on China-sourced inventory, this level of transparency can mean the difference between profitable sourcing and costly mistakes.
            </p>
            <p className="text-foreground-700 text-base leading-relaxed">
              The spreadsheet covers <strong>14 major product categories</strong> — from Shoes and Hoodies/Sweaters to Bags, Belts, and Perfumes — each with its own dedicated section containing verified suppliers, current pricing data, and direct links to product listings. Rather than spending hours cross-referencing information across different websites, you can access everything you need from a single dashboard.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              How the AllChinaBuy Spreadsheet Compares to PandaBuy and Other Agents
            </h2>
            <p className="text-foreground-700 text-base leading-relaxed mb-4">
              Many users come to the <strong>AllChinaBuy Spreadsheet</strong> after trying popular platforms like PandaBuy, Superbuy, CSSBuy, or Wegobuy. While these platforms serve their purpose as buying agents, they each have fundamental limitations that a spreadsheet-based approach solves. PandaBuy, for example, offers a polished user interface but locks supplier data behind product-specific pages — making side-by-side agent comparison nearly impossible. With the <strong>AllChinaBuy Spreadsheet</strong>, you can compare multiple agents across identical criteria: pricing, shipping speed, reliability rating, and communication responsiveness — all in a single view.
            </p>
            <p className="text-foreground-700 text-base leading-relaxed mb-4">
              Another advantage of the <strong>AllChinaBuy Spreadsheet</strong> over traditional platforms is update frequency. Most agent sites update their listings irregularly — sometimes going weeks or months without refreshing supplier data. The AllChinaBuy system, by contrast, follows a weekly update schedule that ensures pricing information remains current and supplier availability is always accurate. In the fast-moving world of China sourcing, where supplier reliability can shift overnight, having access to fresh data is not a luxury — it is a necessity.
            </p>
            <p className="text-foreground-700 text-base leading-relaxed">
              The spreadsheet also addresses a major pain point for international buyers: the language barrier and platform complexity of 1688 and Taobao. While 1688 offers the lowest prices for China-sourced goods, its Chinese-only interface, complex registration requirements, and lack of international shipping support make it nearly impossible for most overseas buyers to use directly. The <strong>AllChinaBuy Spreadsheet</strong> bridges this gap by connecting you to verified agents who handle translation, ordering, quality inspection, and international shipping — while still giving you the transparency to compare costs and make informed decisions.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Step-by-Step: How to Use the AllChinaBuy Spreadsheet for Your First Order
            </h2>
            <p className="text-foreground-700 text-base leading-relaxed mb-4">
              Getting started with the <strong>AllChinaBuy Spreadsheet</strong> is straightforward. Here is a detailed walkthrough of the process from initial access to completed order:
            </p>

            <div className="space-y-5 mt-6">
              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-950 text-lg mb-2">
                  Step 1: Access the Spreadsheet Dashboard
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Navigate to the main <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium">AllChinaBuy Sheet platform</a> and open the live spreadsheet. The dashboard presents an organized overview of all available product categories with quick-jump navigation to each section. Take a moment to familiarize yourself with the layout — each category tab contains supplier listings, pricing columns, agent comparison rows, and direct links to product pages.
                </p>
              </div>

              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-950 text-lg mb-2">
                  Step 2: Filter by Your Target Category
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Use the category navigation to jump to your product of interest — whether that is <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium">Shoes</a>, <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium">Hoodies/Sweaters</a>, <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium">T-Shirts</a>, or any of the 14 available categories. Each category section in the spreadsheet shows verified suppliers specific to that product type, with pricing data updated weekly.
                </p>
              </div>

              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-950 text-lg mb-2">
                  Step 3: Compare Agents and Pricing
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  The most powerful feature of the AllChinaBuy Spreadsheet is the agent comparison view. For each product, you can see how different buying agents compare on price, shipping time, service fees, and reliability. Look for agents with high reliability ratings and competitive pricing — the spreadsheet makes these comparisons intuitive with color-coded indicators and sortable columns.
                </p>
              </div>

              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-950 text-lg mb-2">
                  Step 4: Place Your Order Through the Main Platform
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Once you have identified the best supplier and agent combination for your needs, follow the direct link from the spreadsheet to the main platform to complete your order. The <strong>AllChinaBuy Spreadsheet</strong> serves as your research and decision-making tool, while the <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium">main platform</a> handles the actual transaction processing, order tracking, and shipping logistics.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Common China Sourcing Mistakes the AllChinaBuy Spreadsheet Helps You Avoid
            </h2>
            <p className="text-foreground-700 text-base leading-relaxed mb-4">
              After analyzing thousands of sourcing experiences from the community, we have identified the most common pitfalls that cost resellers and dropshippers time and money. The <strong>AllChinaBuy Spreadsheet</strong> is specifically designed to help you avoid each of these:
            </p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-accent-500 text-lg flex-shrink-0 mt-0.5"></i>
                <div>
                  <strong className="text-foreground-900">Trusting unverified suppliers:</strong>
                  <span className="text-foreground-600"> The spreadsheet only lists suppliers that have been vetted through a multi-step verification process, eliminating the risk of working with fraudulent sellers.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-accent-500 text-lg flex-shrink-0 mt-0.5"></i>
                <div>
                  <strong className="text-foreground-900">Overpaying on agent fees:</strong>
                  <span className="text-foreground-600"> By comparing multiple agents side-by-side, you can identify which ones offer the best value for your specific product type and order volume.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-accent-500 text-lg flex-shrink-0 mt-0.5"></i>
                <div>
                  <strong className="text-foreground-900">Ignoring shipping cost variables:</strong>
                  <span className="text-foreground-600"> The spreadsheet includes shipping estimates from multiple carriers, helping you factor logistics costs into your profit calculations before committing to an order.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-accent-500 text-lg flex-shrink-0 mt-0.5"></i>
                <div>
                  <strong className="text-foreground-900">Relying on outdated pricing:</strong>
                  <span className="text-foreground-600"> With weekly updates, the spreadsheet ensures your sourcing decisions are based on current market data rather than stale pricing that may no longer reflect reality.</span>
                </div>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              What Product Categories Are Covered in the AllChinaBuy Spreadsheet?
            </h2>
            <p className="text-foreground-700 text-base leading-relaxed mb-4">
              The <strong>AllChinaBuy Spreadsheet</strong> currently covers 14 distinct product categories, each with dedicated supplier listings and agent comparison data. Here is the complete breakdown:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
              {[
                { name: "Shoes", url: "https://kaspreadsheet.com/category-911-Shoes.html" },
                { name: "Hoodies/Sweaters", url: "https://kaspreadsheet.com/search-912/name/Shoes.html" },
                { name: "T-Shirts", url: "https://kaspreadsheet.com/search-913/name/Shoes.html" },
                { name: "Jackets", url: "https://kaspreadsheet.com/search-914/name/Shoes.html" },
                { name: "Pants/Shorts", url: "https://kaspreadsheet.com/search-915/name/Shoes.html" },
                { name: "Headwear", url: "https://kaspreadsheet.com/search-916/name/Shoes.html" },
                { name: "Sets", url: "https://kaspreadsheet.com/search-917/name/Shoes.html" },
                { name: "Underwear/Underpants", url: "https://kaspreadsheet.com/search-918/name/Shoes.html" },
                { name: "Jersey", url: "https://kaspreadsheet.com/search-919/name/Shoes.html" },
                { name: "Accessories", url: "https://kaspreadsheet.com/search-920/name/Shoes.html" },
                { name: "Bags", url: "https://kaspreadsheet.com/search-921/name/Shoes.html" },
                { name: "Belts", url: "https://kaspreadsheet.com/search-922/name/Shoes.html" },
                { name: "Perfumes", url: "https://kaspreadsheet.com/search-923/name/Shoes.html" },
                { name: "Other Stuff", url: "https://kaspreadsheet.com/search-924/name/Shoes.html" },
              ].map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="bg-background-100 rounded-lg px-4 py-3 text-sm text-foreground-700 hover:text-primary-700 hover:bg-primary-50 transition-colors border border-background-200/70"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Why the AllChinaBuy Spreadsheet Is Optimized for SEO and Discoverability
            </h2>
            <p className="text-foreground-700 text-base leading-relaxed mb-4">
              One of the strategic advantages of the <strong>AllChinaBuy Spreadsheet</strong> system is its architecture as a comparison-based SEO authority site. Google's search algorithm increasingly favors structured data, comparison tables, and long-form informational content — all of which are built into the core design of this platform. By organizing China sourcing information into a spreadsheet format with clear category structures, the site naturally aligns with Google's Helpful Content System and E-E-A-T (Experience, Expertise, Authoritativeness, Trustworthiness) guidelines.
            </p>
            <p className="text-foreground-700 text-base leading-relaxed mb-4">
              The spreadsheet targets high-intent search queries that users actively type into Google when looking for China sourcing solutions. Keywords like <strong>"best China buying spreadsheet,"</strong> <strong>"PandaBuy alternative spreadsheet,"</strong> <strong>"verified China supplier list 2026,"</strong> and <strong>"agent comparison table China"</strong> are all terms where Google has historically rewarded sites that present comparative, structured data over simple affiliate-style articles. The internal linking structure — connecting the homepage, category pages, and this guide — further strengthens topical authority signals that search engines use to determine ranking positions.
            </p>
            <p className="text-foreground-700 text-base leading-relaxed">
              For users new to China sourcing, the <strong>AllChinaBuy Spreadsheet</strong> serves as both an educational resource and a practical tool. The combination of the spreadsheet dashboard, the category-specific product pages, and this comprehensive guide creates a content ecosystem that answers user questions at every stage of the sourcing journey — from "what is the best way to source from China" all the way to "which specific agent should I use for shoes." This full-funnel content strategy is exactly what Google rewards with higher organic rankings.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Frequently Asked Questions About the AllChinaBuy Spreadsheet
            </h2>
            <div className="space-y-4">
              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-950 text-base mb-2">
                  Is the AllChinaBuy Spreadsheet free to use?
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Access to the spreadsheet data is available through the main AllChinaBuy platform. The spreadsheet serves as your research and comparison tool, while actual orders are processed on the main site where standard platform fees apply.
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-950 text-base mb-2">
                  How often is supplier data updated?
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  The AllChinaBuy Spreadsheet follows a weekly update schedule. Pricing data, supplier availability, and agent comparison metrics are refreshed every seven days to ensure the information you are working with is current and accurate.
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-950 text-base mb-2">
                  Can I use the AllChinaBuy Spreadsheet for any product type?
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  The spreadsheet currently covers 14 major product categories spanning fashion, accessories, and lifestyle products. If your target product falls outside these categories, you can still use the agent comparison data to identify reliable buying agents for your specific sourcing needs.
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-950 text-base mb-2">
                  How does the AllChinaBuy Spreadsheet differ from using an agent directly?
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Using an agent directly means you are limited to that single agent's pricing, shipping options, and supplier relationships. The spreadsheet approach lets you compare multiple agents simultaneously, so you can choose the best option for each specific order rather than being locked into one provider.
                </p>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 p-6 bg-primary-50 rounded-2xl border border-primary-200 text-center">
          <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
            Ready to Start Sourcing Smarter?
          </h3>
          <p className="text-foreground-600 text-sm mb-5 max-w-lg mx-auto">
            Access the AllChinaBuy Spreadsheet now and join thousands of resellers who have transformed their China sourcing workflow.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-database-2-line text-base"></i>
              AllChinaBuy Sheet
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 border border-background-300/60 text-foreground-700 font-medium text-sm rounded-full hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line text-base"></i>
              Back to Homepage
            </Link>
          </div>
        </div>
      </article>

      <footer className="bg-secondary-100 py-8 border-t border-background-200/70">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-950 transition-colors mb-3">
            <i className="ri-arrow-left-line text-base"></i>
            Return to AllChinaBuy Spreadsheet Home
          </Link>
          <p className="text-foreground-500 text-sm">
            &copy; {new Date().getFullYear()} AllChinaBuy Spreadsheet. Your trusted China sourcing intelligence platform.
          </p>
        </div>
      </footer>
    </div>
  );
}